import {
  blankProject,
  component,
  defaults,
  pins,
  isSequential,
  isFF,
  sourceKey,
} from "./registry";
import { validate } from "./validation";
import { compactLayout } from "./layout";
export function random(seed) {
  let a = 2166136261;
  for (const c of String(seed)) a = Math.imul(a ^ c.charCodeAt(0), 16777619);
  return () => {
    a += 0x6d2b79f5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export const defaultGenerator = () => ({
  seed: "260",
  complexity: "medium",
  feedback: true,
  inputCount: 3,
  clockCount: 1,
  initial: 0,
  policy: "X",
  trigger: "POS",
  validEncoders: false,
  counts: { D_FF: 1, JK_FF: 1, T_FF: 1, AND: 1, XOR: 1, NOT: 1 },
  options: {},
  clocks: [{ period: 4, duty: 0.5, phase: 0, initial: 0 }],
});
export const autoLayout = compactLayout;
export function generate(settings) {
  const cfg = { ...defaultGenerator(), ...settings },
    rng = random(cfg.seed),
    p = blankProject();
  p.metadata = {
    title: "Circuit exercise",
    seed: String(cfg.seed),
    generator: JSON.parse(JSON.stringify(cfg)),
  };
  const counts = Object.entries(cfg.counts).filter(([, n]) => n > 0);
  if (
    counts.some(
      ([t, n]) => !Number.isInteger(n) || n > 24 || n < 0 || t === "CLOCK",
    )
  )
    throw new Error("Use 0–24 of each component. Set clocks in Clock sources.");
  const total = counts.reduce((s, [, n]) => s + n, 0);
  if (total === 0 || total > 80)
    throw new Error("Choose between 1 and 80 logic/sequential components.");
  if (
    !Number.isInteger(cfg.inputCount) ||
    cfg.inputCount < 1 ||
    cfg.inputCount > 64
  )
    throw new Error("Choose 1–64 primary inputs.");
  if (
    !Number.isInteger(cfg.clockCount) ||
    cfg.clockCount < 0 ||
    cfg.clockCount > 8
  )
    throw new Error("Choose 0–8 clocks.");
  let serial = 0;
  const create = (type, index, config) => {
    const c = component(type, index, 0, 0, config);
    c.id = `c${++serial}`;
    return c;
  };
  for (let i = 0; i < cfg.inputCount; i++)
    p.inputs.push({
      id: `p${i}`,
      name: i < 3 ? ["P", "Q", "R"][i] : `P${i + 1}`,
      values: Array.from({ length: 16 }, () => (rng() < 0.5 ? 0 : 1)),
    });
  for (let i = 0; i < cfg.clockCount; i++)
    p.components.push(
      create("CLOCK", i + 1, {
        ...defaults("CLOCK"),
        ...(cfg.clocks[i] || cfg.clocks[0] || {}),
      }),
    );
  const logic = [];
  for (const [type, n] of counts)
    for (let i = 0; i < n; i++)
      logic.push(
        create(type, i + 1, {
          initial:
            cfg.initial === "random" ? (rng() < 0.5 ? 0 : 1) : cfg.initial,
          policy: cfg.policy,
          trigger: cfg.trigger,
          ...(cfg.options[type] || {}),
          ...(cfg.perInstance?.[`${type}:${i}`] || {}),
        }),
      );
  // Fisher–Yates makes topology genuinely seed dependent.
  for (let i = logic.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [logic[i], logic[j]] = [logic[j], logic[i]];
  }
  p.components.push(...logic);
  const sequential = logic.filter(isSequential),
    comb = logic.filter((c) => !isSequential(c)),
    clocks = p.components.filter((c) => c.type === "CLOCK");
  if (sequential.length && !clocks.length)
    throw new Error("Sequential components need at least one clock source.");
  const dataPins = logic.reduce(
    (sum, c) =>
      sum +
      pins(c).inputs.filter((pin) => !isSequential(c) || !pin.control).length,
    0,
  );
  if (cfg.inputCount > dataPins)
    throw new Error(
      `There are ${dataPins} data pins for ${cfg.inputCount} primary inputs. Reduce primary inputs or add components so every input is used.`,
    );
  const prim = p.inputs.map((i) => ({ input: i.id })),
    pick = (arr) => arr[Math.floor(rng() * arr.length)],
    outputs = (c) =>
      pins(c).outputs.map((o) => ({ component: c.id, pin: o.id }));
  sequential.forEach((c, i) => {
    c.config.output = i < 26 ? String.fromCharCode(65 + i) : `Q${i + 1}`;
    c.config.pinLabels = Object.fromEntries(
      pins(c).inputs.map((pin) => [
        pin.id,
        pin.control ? "clk" : pin.id + c.config.output.toLowerCase(),
      ]),
    );
  });
  const usedPrim = new Set();
  let wire = 0,
    remaining = dataPins;
  const connect = (from, c, pin) => {
    p.connections.push({
      id: `w${++wire}`,
      from,
      to: { component: c.id, pin },
      width: 1,
    });
    if (from.input) usedPrim.add(from.input);
  };
  const choose = (candidates, used, preferPrimary = true) => {
    const unused = prim.filter((s) => !usedPrim.has(s.input));
    const unique = candidates.filter(
      (s) =>
        !used.has(sourceKey(s)) &&
        (!s.component || !used.has(`node:${s.component}`)),
    );
    const available = unique.length ? unique : candidates;
    const source =
      unused.length && (preferPrimary || remaining <= unused.length)
        ? pick(unused)
        : pick(available);
    remaining--;
    used.add(sourceKey(source));
    if (source.component) used.add(`node:${source.component}`);
    return source;
  };
  const pool = [...prim];
  if (cfg.feedback) pool.push(...sequential.filter(isFF).flatMap(outputs));
  const probability =
    { easy: 0, medium: 0.6, hard: 0.95 }[cfg.complexity] ?? 0.6;
  let encoderInputIndex = 0;
  for (const c of comb) {
    const previous = pool.filter(
      (s) => s.component && !sequential.some((q) => q.id === s.component),
    );
    const used = new Set();
    for (const [idx, pin] of pins(c).inputs.entries()) {
      let from;
      if (c.type === "ENCODER" && cfg.validEncoders) {
        if (encoderInputIndex >= prim.length)
          throw new Error(
            "Valid encoder generation needs a separate primary input per encoder data pin. Increase the primary-input count.",
          );
        from = prim[encoderInputIndex++];
        remaining--;
      } else if (idx === 0 && previous.length && rng() < probability)
        from = choose(previous, used, false);
      else from = choose(rng() < probability ? pool : prim, used, true);
      connect(from, c, pin.id);
    }
    pool.push(...outputs(c));
  }
  sequential.forEach((c, i) => {
    const used = new Set();
    const compatible = clocks.filter(
      (clock) =>
        !clock.config.activation ||
        (c.type === "SR_LATCH"
          ? clock.config.activation.endsWith("LEVEL")
          : clock.config.activation.endsWith("EDGE")),
    );
    if (!compatible.length)
      throw new Error(
        `${c.name} needs ${c.type === "SR_LATCH" ? "a level" : "an edge"} clock. Change a clock type or add another clock source.`,
      );
    const clock = compatible[i % compatible.length];
    if (clock.config.activation)
      c.config.trigger = clock.config.activation.startsWith("NEG")
        ? "NEG"
        : "POS";
    for (const pin of pins(c).inputs) {
      if (pin.control) connect({ component: clock.id, pin: "CLK" }, c, pin.id);
      else {
        const candidates = pool.filter(
          (s) =>
            cfg.feedback ||
            !s.component ||
            !sequential.some((q) => q.id === s.component),
        );
        const combSources = candidates.filter(
          (s) => s.component && !sequential.some((q) => q.id === s.component),
        );
        connect(
          choose(
            combSources.length && rng() < 0.8 ? combSources : candidates,
            used,
            true,
          ),
          c,
          pin.id,
        );
      }
    }
    if (!cfg.feedback) pool.push(...outputs(c));
  });
  // Every chosen component has observable outputs; none is silently dropped.
  p.observables = logic.flatMap((c) =>
    outputs(c)
      .map((source) => ({ id: sourceKey(source), source })),
  );
  const result = autoLayout(p);
  if (cfg.validEncoders) randomizeInputs(result, `${cfg.seed}:inputs`, true);
  const check = validate(result);
  if (!check.valid)
    throw new Error(
      check.issues
        .filter((i) => i.severity === "error")
        .map((i) => i.message)
        .join("\n"),
    );
  return result;
}
export function randomizeInputs(project, seed, validEncoders = false) {
  const rng = random(seed),
    count = Math.ceil(project.timing.duration / project.timing.step);
  for (const input of project.inputs) {
    input.values = Array.from({ length: count }, () => (rng() < 0.5 ? 0 : 1));
    delete input.events;
  }
  if (validEncoders) {
    const claimed = new Set();
    for (const c of project.components.filter((c) => c.type === "ENCODER")) {
      const sources = pins(c).inputs.map(
        (pin) =>
          project.connections.find(
            (e) => e.to.component === c.id && e.to.pin === pin.id,
          )?.from,
      );
      if (
        sources.some((s) => !s?.input) ||
        new Set(sources.map((s) => s.input)).size !== sources.length ||
        sources.some((s) => claimed.has(s.input))
      )
        throw new Error(
          `${c.name}: one-hot generation needs distinct primary-input wires for every data pin, not shared with another encoder. Rewire or use unrestricted inputs.`,
        );
      sources.forEach((s) => claimed.add(s.input));
      for (let t = 0; t < count; t++) {
        const chosen = Math.floor(rng() * sources.length);
        sources.forEach(
          (s, i) =>
            (project.inputs.find((p) => p.id === s.input).values[t] =
              i === chosen ? 1 : 0),
        );
      }
    }
  }
  return project;
}
